// src/modules/app-views/leads/pages/Init/modals/ImportLeadsModal/Step3.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Flex, Typography, Radio, Form, Alert, Progress, theme } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import translateX from 'utils/translateX';
import buildLeadPayload from './helpers/buildLeadPayload';
import validateRow from './helpers/validateRow';

const { Text } = Typography;
const PREVIEW_LIMIT = 10;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step3Content = ({ rows, mapping, importProgress }) => {
  const { token } = theme.useToken();

  const { preview, validCount, invalidCount } = useMemo(() => {
    const valid = [];
    const invalid = [];
    (rows || []).forEach((row, idx) => {
      const payload = buildLeadPayload(row, mapping, idx + 2);
      const { valid: isValid, reasons } = validateRow(payload);
      if (isValid) valid.push(payload);
      else invalid.push({ ...payload, _reasons: reasons });
    });

    const all = [...valid.slice(0, PREVIEW_LIMIT), ...invalid.slice(0, Math.max(0, PREVIEW_LIMIT - valid.length))];
    return { preview: all.slice(0, PREVIEW_LIMIT), validCount: valid.length, invalidCount: invalid.length };
  }, [rows, mapping]);

  const columns = [
    { title: translateX('row'), dataIndex: '_row', width: 60 },
    { title: translateX('name'), dataIndex: 'name', ellipsis: true },
    { title: translateX('field_cpf'), dataIndex: 'cpf', width: 130 },
    { title: translateX('field_email'), dataIndex: 'email', ellipsis: true },
    { title: translateX('field_phone'), dataIndex: 'phone', width: 140 },
    {
      title: translateX('field_interest'),
      dataIndex: 'interest',
      ellipsis: true,
      render: v => v || <Text type="secondary">—</Text>,
    },
    {
      title: translateX('status'),
      dataIndex: '_reasons',
      width: 80,
      render: reasons =>
        reasons?.length
          ? <CloseCircleOutlined style={{ color: token.colorError }} title={reasons.join(', ')} />
          : <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
    },
  ];

  const isImporting = Boolean(importProgress);
  const percent = isImporting && importProgress.total > 0
    ? Math.round((importProgress.processed / importProgress.total) * 100)
    : 0;

  return (
    <Flex vertical gap="middle" className="mt-3">
      <Flex gap="large" align="center" wrap="wrap">
        <Flex align="center" gap={6}>
          <CheckCircleOutlined style={{ color: token.colorSuccess }} />
          <Text strong>{validCount}</Text>
          <Text type="secondary">{translateX('rows_valid')}</Text>
        </Flex>
        <Flex align="center" gap={6}>
          <WarningOutlined style={{ color: token.colorError }} />
          <Text strong>{invalidCount}</Text>
          <Text type="secondary">{translateX('rows_invalid')}</Text>
        </Flex>
      </Flex>

      <SimpleTable
        rowKey="_row"
        size="small"
        columns={columns}
        dataSource={preview}
        pagination={false}
        bordered
      />

      <Form.Item name="conflictStrategy" label={translateX('on_duplicates')} initialValue="ignore">
        <Radio.Group>
          <Radio value="ignore">{translateX('dedup_ignore')}</Radio>
          <Radio value="overwrite">{translateX('dedup_overwrite')}</Radio>
          <Radio value="append">{translateX('dedup_append')}</Radio>
        </Radio.Group>
      </Form.Item>

      {isImporting && (
        <Alert
          type="info"
          showIcon
          title={translateX('import_in_progress')}
          description={
            <Flex vertical gap="small">
              <Progress percent={percent} status={importProgress?.finished ? 'success' : 'active'} />
              <Text type="secondary">
                {importProgress.processed} / {importProgress.total} — {translateX('created')}: {importProgress.created || 0}, {translateX('skipped')}: {importProgress.skipped || 0}, {translateX('errors')}: {importProgress.errors || 0}
              </Text>
            </Flex>
          }
        />
      )}

      {invalidCount > 0 && !isImporting && (
        <Alert
          type="warning"
          showIcon
          title={translateX('some_rows_will_be_skipped', { count: invalidCount })}
        />
      )}
    </Flex>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const importLeadsStep3 = ({ rows, mapping, importProgress }) => ({
  stepTitle: 'preview',
  content: () => <Step3Content rows={rows} mapping={mapping} importProgress={importProgress} />,
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default importLeadsStep3;
